import React from "react";
import { createPortal } from "react-dom";
import { useTheme } from "@app/styles/themes";
import { OverlayRuntimeContext } from "./OverlayContext";
import {
    focusFirstFocusable,
    getOverlayBackdropZIndex,
    getOverlaySurfaceZIndex,
    getTopmostBlockingIndex,
    isOverlayEntryInteractive,
    trapFocusInContainer,
} from "./overlayStackUtils";
import type { OverlayCloseReason, OverlayEntry, OverlayRuntimeValue } from "./types";

const OVERLAY_Z_INDEX_FALLBACK = 1300;

interface OverlayHostProps {
    entries: OverlayEntry[];
    portalContainer?: Element | DocumentFragment;
    closeOverlay: (id: string, reason?: OverlayCloseReason) => void;
    requestDismissOverlay: (id: string) => void;
    focusOverlay: (id: string) => void;
    registerSurfaceElement: (id: string, element: HTMLElement | null) => void;
}

interface OverlaySurfaceHostProps {
    entry: OverlayEntry;
    isTopmost: boolean;
    isInteractive: boolean;
    zIndex: number;
    closeOverlay: (id: string, reason?: OverlayCloseReason) => void;
    focusOverlay: (id: string) => void;
    registerSurfaceElement: (id: string, element: HTMLElement | null) => void;
}

const OverlaySurfaceHost: React.FC<OverlaySurfaceHostProps> = ({
    entry,
    isTopmost,
    isInteractive,
    zIndex,
    closeOverlay,
    focusOverlay,
    registerSurfaceElement,
}) => {
    const surfaceRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        registerSurfaceElement(entry.id, isInteractive ? surfaceRef.current : null);
        return () => {
            registerSurfaceElement(entry.id, null);
        };
    }, [entry.id, isInteractive, registerSurfaceElement]);

    React.useEffect(() => {
        if (!isTopmost || !isInteractive) return;

        const activeElement = document.activeElement;
        const surfaceElement = surfaceRef.current;
        if (activeElement instanceof HTMLElement && surfaceElement != null && surfaceElement.contains(activeElement)) {
            return;
        }

        const focusId = window.requestAnimationFrame(() => {
            const element = surfaceRef.current;
            if (element == null) return;

            if (entry.blocking) {
                focusFirstFocusable(element);
                return;
            }

            element.focus({ preventScroll: true });
        });

        return () => {
            window.cancelAnimationFrame(focusId);
        };
    }, [entry.blocking, isInteractive, isTopmost]);

    React.useEffect(() => {
        if (!isTopmost || !isInteractive || !entry.blocking) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            const surfaceElement = surfaceRef.current;
            if (surfaceElement == null) return;
            trapFocusInContainer(event, surfaceElement);
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [entry.blocking, isInteractive, isTopmost]);

    const runtimeValue = React.useMemo<OverlayRuntimeValue>(
        () => ({
            id: entry.id,
            zIndex,
            surface: entry.surface,
            blocking: entry.blocking,
            isTopmost,
            placement: entry.placement,
            close: (reason) => closeOverlay(entry.id, reason),
            focus: () => focusOverlay(entry.id),
            resolveValue: entry.resolveValue,
        }),
        [
            entry.blocking,
            entry.id,
            entry.placement,
            entry.resolveValue,
            entry.surface,
            isTopmost,
            zIndex,
            closeOverlay,
            focusOverlay,
        ],
    );

    React.useEffect(() => {
        const element = surfaceRef.current;
        if (element == null) return;

        if (!isInteractive) {
            element.setAttribute("inert", "");
            return;
        }

        element.removeAttribute("inert");
    }, [isInteractive]);

    const handleMouseDown = () => {
        if (!isInteractive) return;
        focusOverlay(entry.id);
    };

    return (
        <OverlayRuntimeContext.Provider value={runtimeValue}>
            <div
                ref={surfaceRef}
                tabIndex={isInteractive ? -1 : undefined}
                aria-hidden={isInteractive ? undefined : true}
                data-overlay-surface={entry.surface}
                data-overlay-placement={entry.placement}
                data-overlay-interactive={isInteractive ? "true" : "false"}
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex,
                    pointerEvents: "none",
                }}
                onMouseDown={handleMouseDown}
            >
                <div style={{ pointerEvents: isInteractive ? "auto" : "none" }}>{entry.render()}</div>
            </div>
        </OverlayRuntimeContext.Provider>
    );
};

/**
 * Рендерит overlay-стек в portal (`document.body` по умолчанию).
 */
export const OverlayHost: React.FC<OverlayHostProps> = ({
    entries,
    portalContainer = document.body,
    closeOverlay,
    requestDismissOverlay,
    focusOverlay,
    registerSurfaceElement,
}) => {
    const { theme } = useTheme();
    const overlayBaseZIndex = theme.tokens.zIndex.overlay ?? OVERLAY_Z_INDEX_FALLBACK;
    const backdropBackground =
        theme.tokens.color.overlay.backdrop ?? `color-mix(in srgb, ${theme.tokens.color.common.black} 55%, transparent)`;
    const topmostBlockingIndex = getTopmostBlockingIndex(entries);

    return createPortal(
        <>
            {topmostBlockingIndex !== -1 && (
                <div
                    aria-hidden="true"
                    className="overlay-backdrop"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: getOverlayBackdropZIndex(overlayBaseZIndex, topmostBlockingIndex),
                        background: backdropBackground,
                    }}
                    onClick={() => {
                        const blockingEntry = entries[topmostBlockingIndex];
                        if (blockingEntry?.closeOnBackdropClick !== true) return;
                        requestDismissOverlay(blockingEntry.id);
                    }}
                />
            )}
            {entries.map((entry, index) => (
                <OverlaySurfaceHost
                    key={entry.id}
                    entry={entry}
                    isTopmost={index === entries.length - 1}
                    isInteractive={isOverlayEntryInteractive(index, topmostBlockingIndex)}
                    zIndex={getOverlaySurfaceZIndex(overlayBaseZIndex, index)}
                    closeOverlay={closeOverlay}
                    focusOverlay={focusOverlay}
                    registerSurfaceElement={registerSurfaceElement}
                />
            ))}
        </>,
        portalContainer,
    );
};
